// src/components/layout/queue-counts.ts
import { useQuery } from '@tanstack/react-query';
import { getPlatformStatus } from '@/api/endpoints/platform-status';
import { queryKeys } from '@/api/query-keys';
import type { PlatformStatus } from '@/api/schemas/platform-status';

/**
 * The number next to a menu entry: how many things wait in that section's
 * queue, read off the same `GET /admin/platform/status` the Panel's
 * «Pendientes de hoy» lists, so the menu and the home can never disagree.
 *
 * `null` means «no number for this entry»: the section has no queue (Config,
 * Auditoría), or the backend cut the card for this reader (a staff member
 * without its capability gets `null`, never a zero that would read as
 * «nothing here»), or the backend predates the card.
 */
export function queueCountFor(status: PlatformStatus, to: string): number | null {
  const q = status.queues;
  const sum = (...parts: Array<number | null | undefined>): number | null =>
    parts.every((p) => p == null) ? null : parts.reduce<number>((a, p) => a + (p ?? 0), 0);
  switch (to) {
    case '/users/kyc':
      return status.kycPending ?? null;
    case '/listings':
      return status.listingsPendingReview ?? null;
    case '/stores':
      return status.storesPending ?? null;
    case '/orders':
      // Orders have no queue of their own: the frozen ones are «Dinero parado».
      return null;
    case '/trades':
      return status.tradesUnderReview ?? null;
    case '/disputes':
      return q?.disputesOpen?.count ?? null;
    case '/kohub':
      return status.kohub
        ? status.kohub.requested + status.kohub.inOffice + status.kohub.incidentsOpen
        : null;
    case '/escrow':
      return status.escrow
        ? status.escrow.frozenOrders + status.escrow.frozenTrades + status.escrow.clawbacksPending
        : null;
    case '/pending-decisions':
      return status.pendingDecisions ?? null;
    case '/moderation':
      return status.reportsOpen ?? null;
    case '/tournaments':
      return status.tournaments?.pendingApproval ?? null;
    case '/questions/proposals':
      return status.proposalsPending ?? null;
    case '/payouts':
      return q?.payoutsPending?.count ?? null;
    case '/outbox':
      return status.outboxStuck ?? null;
    case '/users':
      // The parent, when its children are folded: what waits under it.
      return sum(status.kycPending);
    case '/questions':
      return sum(status.proposalsPending);
    default:
      return null;
  }
}

/**
 * The status for the menu. Shares the Panel's cache key, so opening the home
 * costs no second request; refreshed every two minutes while the panel is
 * open, because a number that is an hour old next to «Disputas» is worse
 * than none.
 */
export function useQueueCounts(enabled = true): PlatformStatus | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.platformStatus(),
    queryFn: getPlatformStatus,
    staleTime: 60_000,
    refetchInterval: 120_000,
    enabled,
  });
  return data;
}
